import React, {useState, useEffect} from "react";
import {
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Chip,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import {GridActionsCellItem} from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUpload from "@mui/icons-material/CloudUpload";
import Delete from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ArticleIcon from "@mui/icons-material/Article";
import QuizIcon from "@mui/icons-material/Quiz";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import {GET, POST, BASE_URL} from "APIclient";
import properties from "constants/ApplicationConstants";
import Tooltip from "@mui/material/Tooltip";
import HRClearanceUpdateForm from "./HRClearanceUpdateForm";
import {useNavigate} from "react-router-dom";

const HRClearanceList = () => {
  const [hrClearances, setHrClearances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [selectedClearance, setSelectedClearance] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackColor, setSnackColor] = useState("success");
  const [snackMessage, setSnackMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  // Exit Clearance Menu
  const [exitClearanceAnchorEl, setExitClearanceAnchorEl] = useState(null);
  const [selectedExitClearanceRow, setSelectedExitClearanceRow] = useState(null);
  const openExitClearanceMenu = Boolean(exitClearanceAnchorEl);

  // Exit Interview Menu
  const [exitInterviewAnchorEl, setExitInterviewAnchorEl] = useState(null);
  const [selectedExitInterviewRow, setSelectedExitInterviewRow] = useState(null);
  const openExitInterviewMenu = Boolean(exitInterviewAnchorEl);

  // Pagination state (server-side like FinanceClearanceList)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Search parameters
  const [searchParams, setSearchParams] = useState({
    employeeId: "",
    employeeName: "",
    status: ""
  });

  // Approve/Reject form data
  const [managerSignature, setManagerSignature] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [errors, setErrors] = useState({});

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  // Status options for dropdown
  const statusOptions = [
    {value: "", label: "All Status"},
    {value: "PENDING", label: "Pending"},
    {value: "APPROVED", label: "Approved"}
    // { value: 'REJECTED', label: 'Rejected' },
    // { value: 'CLEARED', label: 'Cleared' },
  ];

  // Exit Clearance Menu Handlers
  const handleExitClearanceMenu = (event, rowData) => {
    event.stopPropagation();
    setExitClearanceAnchorEl(event.currentTarget);
    setSelectedExitClearanceRow(rowData);
  };

  const handleCloseExitClearanceMenu = () => {
    setExitClearanceAnchorEl(null);
    setSelectedExitClearanceRow(null);
  };

  // Exit Interview Menu Handlers
  const handleExitInterviewMenu = (event, rowData) => {
    event.stopPropagation();
    setExitInterviewAnchorEl(event.currentTarget);
    setSelectedExitInterviewRow(rowData);
  };

  const handleCloseExitInterviewMenu = () => {
    setExitInterviewAnchorEl(null);
    setSelectedExitInterviewRow(null);
  };

  // Download and View handlers for Exit Clearance
  const handleDownloadExitClearance = async () => {
    if (selectedExitClearanceRow) {
      try {
        const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

        if (!token) {
          throw new Error("Authentication token not found. Please log in again.");
        }

        const url = `${BASE_URL}/api/exit/clearance/download/${selectedExitClearanceRow.employeeId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf"
          },
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error(`Exit Clearance not yet submitted`);
        }

        const blob = await response.blob();

        if (blob.size === 0) {
          throw new Error("Received empty PDF file");
        }

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        const employeeName =
          selectedExitClearanceRow.employeeName?.trim().replace(/\s+/g, "_") || "Employee";

        link.download = `${employeeName}-Exit_Clearance.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(link.href), 100);

        setSnackMessage("Exit clearance form downloaded successfully!");
        setSnackColor("success");
        setSnackOpen(true);
      } catch (error) {
        console.error("Download error:", error);
        setSnackMessage(error.message || "Failed to download exit clearance form.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
    handleCloseExitClearanceMenu();
  };

  const handleViewExitClearance = async () => {
    if (selectedExitClearanceRow) {
      try {
        const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

        if (!token) {
          throw new Error("Authentication token not found. Please log in again.");
        }

        const url = `${BASE_URL}/api/exit/clearance/preview/${selectedExitClearanceRow.employeeId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf"
          },
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error(`Exit Clearance not yet submitted`);
        }

        const blob = await response.blob();

        if (blob.size === 0) {
          throw new Error("Received empty PDF file");
        }

        const employeeName =
          selectedExitClearanceRow.employeeName?.trim().replace(/\s+/g, "_") || "Employee";
        const filename = `${employeeName}-Exit_Clearance.pdf`;
        
        // Create object URL
        const fileURL = window.URL.createObjectURL(blob);
        
        // Create a temporary anchor element with download attribute
        const tempLink = document.createElement('a');
        tempLink.href = fileURL;
        tempLink.download = filename;
        tempLink.style.display = 'none';
        document.body.appendChild(tempLink);
        
        // For viewing: open in new tab
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>${filename}</title>
              <style>
                body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; }
                iframe { width: 100%; height: 100%; border: none; }
              </style>
            </head>
            <body>
              <iframe src="${fileURL}" type="application/pdf"></iframe>
            </body>
            </html>
          `);
          newWindow.document.close();
        }
        
        // Clean up
        document.body.removeChild(tempLink);
        setTimeout(() => window.URL.revokeObjectURL(fileURL), 60000);
      } catch (error) {
        console.error("Preview error:", error);
        setSnackMessage(error.message || "Failed to preview exit clearance form.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
    handleCloseExitClearanceMenu();
  };

  // Download and View handlers for Exit Interview
  const handleDownloadExitInterview = async () => {
    if (selectedExitInterviewRow) {
      try {
        const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

        if (!token) {
          throw new Error("Authentication token not found. Please log in again.");
        }

        const url = `${BASE_URL}/api/exit/interview/download/${selectedExitInterviewRow.employeeId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf"
          },
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error(`Exit interview not yet submitted`);
        }

        const blob = await response.blob();

        if (blob.size === 0) {
          throw new Error("Received empty PDF file");
        }

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Exit_Interview_${selectedExitInterviewRow.employeeId}_${
          new Date().toISOString().split("T")[0]
        }.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(link.href), 100);

        setSnackMessage("Exit interview form downloaded successfully!");
        setSnackColor("success");
        setSnackOpen(true);
      } catch (error) {
        console.error("Download error:", error);
        setSnackMessage(error.message || "Failed to download exit interview form.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
    handleCloseExitInterviewMenu();
  };

  const handleViewExitInterview = async () => {
    if (selectedExitInterviewRow) {
      try {
        const token = localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
        const url = `${BASE_URL}/api/exit/interview/preview/${selectedExitInterviewRow.employeeId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf"
          },
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error(`Exit interview not yet submitted`);
        }

        const blob = await response.blob();

        if (blob.size === 0) {
          throw new Error("Received empty PDF file");
        }

        const fileURL = window.URL.createObjectURL(blob);
        window.open(fileURL, "_blank");

        setTimeout(() => window.URL.revokeObjectURL(fileURL), 100);
      } catch (error) {
        console.error("Preview error:", error);
        setSnackMessage(error.message || "Failed to preview exit interview form.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    }
    handleCloseExitInterviewMenu();
  };

  const handleFillExitInterview = () => {
    if (selectedExitInterviewRow) {
      localStorage.setItem("resignationEndDate", selectedExitInterviewRow.relievingDate);
      console.log("The value of exit..", selectedExitInterviewRow);

      navigate("/exitInterviewForm", {
        state: {
          mode: "edit",
          // emplooyeeId:selectedExitClearanceRow.emplooyeeId,
          employeeId: selectedExitInterviewRow.employeeId, // Add this
          employeeName: selectedExitInterviewRow.employeeName,
          resignationId: selectedExitInterviewRow.resignationRequestId
        }
      });
    }
    handleCloseExitInterviewMenu();
  };

  // Add sequential IDs for DataGrid rows when backend doesn't return id field the way we expect
  const transformPaginatedData = (paginatedData, currentPage, pageSize) => {
    return paginatedData.map((item, index) => ({
      ...item,
      id: currentPage * pageSize + index + 1
    }));
  };

  // Fetch all HR clearances (server-side pagination & search)
  const fetchAllHRClearances = async (
    page = 0,
    pageSize = 10,
    employeeId = "",
    employeeName = "",
    status = ""
  ) => {
    setLoading(true);
    try {
      const jwtToken =
        window.sessionStorage.getItem("authToken") || window.sessionStorage.getItem("token");

      const params = new URLSearchParams();
      params.append("page", page);
      params.append("size", pageSize);

      // Build combined 'search' param similar to FinanceClearanceList: employeeId + employeeName
      const searchTerms = [];
      if (employeeId?.trim()) searchTerms.push(employeeId.trim());
      if (employeeName?.trim()) searchTerms.push(employeeName.trim());
      if (searchTerms.length > 0) {
        params.append("search", searchTerms.join(" "));
      }

      if (status?.trim()) params.append("status", status.trim());

      const url = `api/exit/clearance/hr?${params.toString()}`;

      const response = await GET(url, {
        headers: {
          Authorization: `Bearer ${jwtToken}`
        }
      });

      if (response.status === 200) {
        // Try to support both shapes:
        // 1) response.data.data.paginatedData (SuccessResponse<PaginatedResponse<...>>)
        // 2) response.data.paginatedData (older/other shape)
        const paginatedData =
          response.data?.data?.paginatedData ?? response.data?.paginatedData ?? [];
        const total = response.data?.data?.totalItems ?? response.data?.totalItems ?? 0;
        const pages = response.data?.data?.totalPages ?? response.data?.totalPages ?? 0;

        const transformed = transformPaginatedData(paginatedData, page, pageSize);
        setHrClearances(transformed);
        setTotalItems(total);
        setTotalPages(pages);
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || "Failed to load HR clearances");
      setSnackColor("error");
      setSnackOpen(true);
      setHrClearances([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Search specific HR clearance (server-side search)
  const handleSearch = async () => {
    setSearchLoading(true);
    // Reset to first page when searching
    setPaginationModel({...paginationModel, page: 0});

    await fetchAllHRClearances(
      0,
      paginationModel.pageSize,
      searchParams.employeeId,
      searchParams.employeeName,
      searchParams.status
    );

    setSearchLoading(false);
    setSnackMessage("Search completed successfully");
    setSnackColor("success");
    setSnackOpen(true);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle reset search
  const handleReset = () => {
    setSearchParams({
      employeeId: "",
      employeeName: "",
      status: ""
    });
    setPaginationModel({page: 0, pageSize: 10});
    fetchAllHRClearances(0, 10, "", "", "");
  };

  // Handle pagination change (server-side)
  const handlePaginationChange = newModel => {
    setPaginationModel(newModel);
    fetchAllHRClearances(
      newModel.page,
      newModel.pageSize,
      searchParams.employeeId,
      searchParams.employeeName,
      searchParams.status
    );
  };

  // View/update/approve/reject handlers
  const handleView = row => {
    setSelectedClearance(row);
    setOpenViewModal(true);
  };

  const handleUpdate = row => {
    setSelectedClearance(row);
    setOpenUpdateModal(true);
  };

  const handleApprove = row => {
    setSelectedClearance(row);
    setManagerSignature(null);
    setErrors({});
    setOpenApproveModal(true);
  };

  const handleReject = row => {
    setSelectedClearance(row);
    setManagerSignature(null);
    setRejectReason("");
    setErrors({});
    setOpenRejectModal(true);
  };

  const submitApprove = async () => {
    if (!managerSignature) {
      setErrors({signature: "Manager signature is required"});
      return;
    }

    setActionLoading(true);
    try {
      const jwtToken =
        window.sessionStorage.getItem("authToken") || window.sessionStorage.getItem("token");

      const requestBody = {
        managerSignature: managerSignature
      };

      const response = await POST(
        `api/exit/clearance/hr/${selectedClearance.exitClearanceId}/approve`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`
          }
        }
      );

      if (response.status === 200) {
        setSnackMessage("HR clearance approved successfully");
        setSnackColor("success");
        setSnackOpen(true);
        setOpenApproveModal(false);
        // refresh using current pagination + search params
        fetchAllHRClearances(
          paginationModel.page,
          paginationModel.pageSize,
          searchParams.employeeId,
          searchParams.employeeName,
          searchParams.status
        );
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || "Failed to approve HR clearance");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  const submitReject = async () => {
    if (!managerSignature) {
      setErrors({signature: "Manager signature is required"});
      return;
    }

    setActionLoading(true);
    try {
      const jwtToken =
        window.sessionStorage.getItem("authToken") || window.sessionStorage.getItem("token");

      const requestBody = {
        managerSignature: managerSignature,
        reason: rejectReason.trim() || null
      };

      const response = await POST(
        `api/exit/clearance/hr/${selectedClearance.exitClearanceId}/reject`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`
          }
        }
      );

      if (response.status === 200) {
        setSnackMessage("HR clearance rejected successfully");
        setSnackColor("success");
        setSnackOpen(true);
        setOpenRejectModal(false);
        // refresh using current pagination + search params
        fetchAllHRClearances(
          paginationModel.page,
          paginationModel.pageSize,
          searchParams.employeeId,
          searchParams.employeeName,
          searchParams.status
        );
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || "Failed to reject HR clearance");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchAllHRClearances(paginationModel.page, paginationModel.pageSize, "", "", "");
  }, []);

  const columns = [
    {
      field: "id",
      headerName: "#",
      width: 70,
      headerAlign: "center",
      align: "center"
    },
    {
      field: "employeeId",
      headerName: "Employee ID",
      width: 130,
      headerAlign: "center",
      align: "center"
    },
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 200
    },
    {
      field: "status",
      headerName: "Clearance Status",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: params => {
        const status = params.value?.toLowerCase() || "";
        let buttonColor;
        let statusText = params.value || "N/A";

        switch (status) {
          case "pending":
          case "no":
            buttonColor = "warning";
            break;
          case "approved":
          case "yes":
          case "cleared":
            buttonColor = "success";
            break;
          case "rejected":
          case "not_cleared":
            buttonColor = "error";
            break;
          case "na":
            buttonColor = "info";
            break;
          default:
            buttonColor = "default";
        }

        return (
          <MDButton
            variant="contained"
            color={buttonColor}
            size="small"
            disabled
            sx={{
              textTransform: "capitalize",
              fontSize: "0.800rem",
              width: "120px",
              justifyContent: "center"
            }}
          >
            {statusText.replace("_", " ")}
          </MDButton>
        );
      }
    },
    {
      field: "documents",
      headerName: "Clearance Form",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: params => {
        return (
          <div
            style={{display: "flex", gap: "8px", alignItems: "center", justifyContent: "center"}}
          >
            <Tooltip title="Exit Clearance" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={e => handleExitClearanceMenu(e, params.row)}
                sx={{
                  padding: "13px",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.08)"
                  }
                }}
              >
                <ArticleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      headerAlign: "center",
      align: "center",
      type: "actions",
      getActions: params => {
        const status = params.row.status?.toLowerCase() || "";
        const actions = [];

        actions.push(
          <Tooltip title="View Form" arrow key="view">
            <GridActionsCellItem
              icon={<VisibilityIcon />}
              label="View"
              color="primary"
              onClick={() => handleView(params.row)}
            />
          </Tooltip>
        );

        if (
          status === "pending" ||
          status === "approved" ||
          status === "cleared" ||
          status === "yes"
        ) {
          actions.push(
            <Tooltip title="Update Form" arrow key="update">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Update"
                color="info"
                onClick={() => handleUpdate(params.row)}
              />
            </Tooltip>
          );
        }

        return actions;
      }
    }
  ];

  const getStatusChip = status => {
    if (!status) return <Chip label="N/A" size="small" />;

    const statusUpper = status.toUpperCase();
    let color = "default";

    if (statusUpper === "APPROVED" || statusUpper === "YES" || statusUpper === "CLEARED") {
      color = "success";
    } else if (statusUpper === "PENDING" || statusUpper === "NO") {
      color = "warning";
    } else if (statusUpper === "REJECTED" || statusUpper === "NOT_CLEARED") {
      color = "error";
    } else if (statusUpper === "NA") {
      color = "info";
    }

    return <Chip label={status.replace("_", " ")} color={color} size="small" />;
  };

  if (loading && paginationModel.page === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
          </MDBox>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <Card>
          <MDBox
            variant="gradient"
            sx={{background: properties.colors.bgcolorbluethem}}
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={2}
            mt={2}
            p={3}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              HR Clearance Management
            </MDTypography>
            <MDTypography variant="body2" color="white" opacity={0.8}>
              View and manage HR clearance records
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            {/* Search Section */}
            <Card sx={{p: 2, mb: 3, backgroundColor: "#f8f9fa"}}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Search HR Clearance
              </MDTypography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={3}>
                  <MDInput
                    fullWidth
                    type="text"
                    label="Employee ID"
                    variant="standard"
                    value={searchParams.employeeId}
                    onChange={e => setSearchParams({...searchParams, employeeId: e.target.value})}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Employee ID"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDInput
                    fullWidth
                    type="text"
                    label="Employee Name"
                    variant="standard"
                    value={searchParams.employeeName}
                    onChange={e => setSearchParams({...searchParams, employeeName: e.target.value})}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter Employee Name"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={searchParams.status}
                      onChange={e => setSearchParams({...searchParams, status: e.target.value})}
                      label="Filter by Status"
                    >
                      {statusOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={searchLoading}
                      sx={{background: properties.colors.bgcolorbluethem}}
                    >
                      {searchLoading ? "Searching..." : "Search"}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RefreshIcon />}
                      onClick={handleReset}
                    >
                      Reset
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>

            {/* Data Grid with server-side pagination */}
            <div style={{height: 500, width: "100%"}}>
              <DataGrid
                rows={hrClearances}
                columns={columns}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                rowCount={totalItems}
                pageSizeOptions={[10, 25, 50]}
                loading={loading}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none"
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f5f5f5"
                  }
                }}
              />
            </div>

            <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="caption" color="text">
                Showing {hrClearances.length} of {totalItems} records
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Page {paginationModel.page + 1} of {totalPages || 1}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        {/* Exit Clearance Menu */}
        <Menu
          anchorEl={exitClearanceAnchorEl}
          open={openExitClearanceMenu}
          onClose={handleCloseExitClearanceMenu}
          PaperProps={{
            elevation: 3,
            sx: {minWidth: 200}
          }}
        >
          <MenuItem onClick={handleViewExitClearance}>
            <ListItemIcon>
              <ArticleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Clearance</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDownloadExitClearance}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download PDF</ListItemText>
          </MenuItem>
        </Menu>

        {/* Exit Interview Menu */}
        <Menu
          anchorEl={exitInterviewAnchorEl}
          open={openExitInterviewMenu}
          onClose={handleCloseExitInterviewMenu}
          PaperProps={{
            elevation: 3,
            sx: {minWidth: 200}
          }}
        >
          <MenuItem onClick={handleViewExitInterview}>
            <ListItemIcon>
              <QuizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Interview</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleFillExitInterview}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {selectedExitInterviewRow?.exitInterviewStatus === "Not Started"
                ? "Fill Interview"
                : "Update Interview"}
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDownloadExitInterview}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download PDF</ListItemText>
          </MenuItem>
        </Menu>

        {/* View Modal (Read-Only) */}
        <HRClearanceUpdateForm
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          clearanceData={selectedClearance}
          readOnly={true}
          onSuccess={() => {
            setOpenViewModal(false);
            fetchAllHRClearances(
              paginationModel.page,
              paginationModel.pageSize,
              searchParams.employeeId,
              searchParams.employeeName,
              searchParams.status
            );
          }}
        />

        {/* Update Modal (Editable) */}
        <HRClearanceUpdateForm
          open={openUpdateModal}
          onClose={() => setOpenUpdateModal(false)}
          clearanceData={selectedClearance}
          readOnly={false}
          onSuccess={() => {
            setOpenUpdateModal(false);
            fetchAllHRClearances(
              paginationModel.page,
              paginationModel.pageSize,
              searchParams.employeeId,
              searchParams.employeeName,
              searchParams.status
            );
          }}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackOpen}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={3000}
          onClose={handleSnackClose}
        >
          <Alert
            onClose={handleSnackClose}
            severity={snackColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snackMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
};

export default HRClearanceList;